import { ChevronDown, Loader2 } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';

import { getInitials, UserAvatar } from '@crossnokaye/ux-common';

import { Popover, PopoverContent, PopoverTrigger } from '@/ux-common/components/popover/popover';
import { cn } from '@/ux-common/lib/utils';

interface ExpandedAccessSelectorProps {
  children: ReactNode;
  isLoading: boolean;
  isViewOnly: boolean;
  name: string;
  role: string;
}

/**
 * The account button at the foot of the expanded left nav: avatar, name, access
 * status and role, opening the access options above it. The menu portals (the
 * registry Popover always does), so it stays anchored to the button whatever
 * the nav around it does, and matches the button's width.
 */
export const ExpandedAccessSelector = ({
  children,
  isLoading,
  isViewOnly,
  name,
  role,
}: ExpandedAccessSelectorProps) => {
  const [open, setOpen] = useState(false);
  const initials = useMemo(() => getInitials(name), [name]);

  return (
    <Popover open={open} onOpenChange={(next) => setOpen(isLoading ? false : next)}>
      <PopoverTrigger
        aria-label={`Access selector for ${name}`}
        className={cn(
          'group flex w-full cursor-pointer items-center gap-3 rounded-sm border-border !border-t-1 bg-muted p-2',
          'hover:bg-tertiary data-[state=open]:bg-tertiary',
          isLoading && 'pointer-events-none'
        )}
        data-component="ExpandedAccessSelector"
        data-testid={'expanded-nav-access-selector'}
      >
        {isLoading ? (
          <>
            <Loader2 aria-label="Loading" className="h-10 w-10 animate-spin" />
            <div className="flex flex-grow flex-col justify-center">
              <div className="mb-2 h-4 w-20 animate-pulse rounded bg-muted-foreground/20" />
              <div className="h-3 w-32 animate-pulse rounded bg-muted-foreground/20" />
            </div>
            <div className="h-5 w-5 animate-pulse rounded bg-muted-foreground/20" />
          </>
        ) : (
          <>
            <UserAvatar
              indicatorClass={isViewOnly ? 'bg-warning' : 'bg-primary'}
              initials={initials}
            />
            <div className="flex flex-grow flex-col justify-center">
              <div className="self-start font-medium text-foreground">{name}</div>
              <div className="flex items-center text-sm">
                <span
                  aria-label="Status"
                  className={cn(
                    'font-bold uppercase',
                    isViewOnly ? 'text-warning' : 'text-primary'
                  )}
                  role="status"
                >
                  {isViewOnly ? 'view only' : 'active'}
                </span>
                <span aria-hidden="true" className="mx-1 text-muted-foreground">
                  •
                </span>
                <span className="font-bold uppercase text-muted-foreground">{role}</span>
              </div>
            </div>
            <ChevronDown
              aria-hidden="true"
              className="text-foreground transition-transform duration-200 group-data-[state=open]:rotate-180"
              size={20}
            />
          </>
        )}
      </PopoverTrigger>
      {/* The registry popover draws no surface, so the options bring their own:
          the popover look from the collapsed nav's menu, at the button's width. */}
      <PopoverContent
        aria-label="Access options"
        className="w-(--radix-popover-trigger-width) flex flex-col rounded-md bg-popover text-popover-foreground shadow-md"
        side="top"
      >
        {children}
      </PopoverContent>
    </Popover>
  );
};
